"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { Token } from "@uniswap/sdk-core";
import { useRef } from "react";
import {
  Chain,
  ProtectionAttackType,
  ProtectionResult,
  SafetyLevel
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { UniverseChainId } from "features/chains/types";
import { fromGraphQLChain, toGraphQLChain } from "features/chains/utils";
import { AttackType, TokenList } from "features/dataApi/types";
import { NativeCurrency } from "features/tokens/NativeCurrency";
import {
  currencyId,
  currencyIdToChain,
  currencyIdToGraphQLAddress,
  isNativeCurrencyAddress
} from "utils/uniswapCurrencyId";
import { sortKeysRecursively } from "utilities/src/primitives/objects";
export function currencyIdToContractInput(id) {
  return {
    // TODO: WALL-4919: Remove hardcoded Mainnet
    chain: toGraphQLChain(currencyIdToChain(id) ?? UniverseChainId.Mainnet) ?? Chain.Ethereum,
    address: currencyIdToGraphQLAddress(id) ?? void 0
  };
}
export function tokenProjectToCurrencyInfos(tokenProjects, chainFilter) {
  return tokenProjects?.flatMap(
    (project) => project?.tokens.map((token) => {
      const { logoUrl, safetyLevel } = project ?? {};
      const { name, chain, address, decimals, symbol, feeData, protectionInfo } = token ?? {};
      const chainId = fromGraphQLChain(chain);
      if (chainFilter && chainFilter !== chainId) {
        return null;
      }
      const currency = buildCurrency({
        chainId,
        address,
        decimals,
        symbol,
        name,
        buyFeeBps: feeData?.buyFeeBps,
        sellFeeBps: feeData?.sellFeeBps
      });
      if (!currency) {
        return null;
      }
      const currencyInfo = buildCurrencyInfo({
        currency,
        currencyId: currencyId(currency),
        logoUrl,
        safetyInfo: getCurrencySafetyInfo(safetyLevel, protectionInfo)
      });
      return currencyInfo;
    })
  ).filter(Boolean);
}
function isNonNativeAddress(chainId, address) {
  return !isNativeCurrencyAddress(chainId, address);
}
const CURRENCY_CACHE = /* @__PURE__ */ new Map();
export function buildCurrency(args) {
  const { chainId, address, decimals, symbol, name, bypassChecksum = true, buyFeeBps, sellFeeBps } = args;
  if (!chainId || decimals === void 0 || decimals === null) {
    return void 0;
  }
  const cacheKey = JSON.stringify(sortKeysRecursively(args));
  const cachedCurrency = CURRENCY_CACHE.get(cacheKey);
  if (cachedCurrency) {
    return cachedCurrency;
  }
  const buyFee = buyFeeBps && BigNumber.from(buyFeeBps).gt(0) ? BigNumber.from(buyFeeBps) : void 0;
  const sellFee = sellFeeBps && BigNumber.from(sellFeeBps).gt(0) ? BigNumber.from(sellFeeBps) : void 0;
  const result = isNonNativeAddress(chainId, address) ? new Token(chainId, address, decimals, symbol ?? void 0, name ?? void 0, bypassChecksum, buyFee, sellFee) : NativeCurrency.onChain(chainId);
  CURRENCY_CACHE.set(cacheKey, result);
  return result;
}
const CURRENCY_INFO_CACHE = /* @__PURE__ */ new Map();
export function buildCurrencyInfo(args) {
  const cacheKey = JSON.stringify(sortKeysRecursively(args));
  const cachedCurrencyInfo = CURRENCY_INFO_CACHE.get(cacheKey);
  if (cachedCurrencyInfo) {
    return cachedCurrencyInfo;
  }
  CURRENCY_INFO_CACHE.set(cacheKey, args);
  return args;
}
function getTokenListFromSafetyLevel(safetyInfo) {
  switch (safetyInfo) {
    case SafetyLevel.Blocked:
      return TokenList.Blocked;
    case SafetyLevel.Verified:
      return TokenList.Default;
    default:
      return TokenList.NonDefault;
  }
}
function getHighestPriorityAttackType(attackTypes) {
  if (!attackTypes || attackTypes.length === 0) {
    return void 0;
  }
  const attackTypeSet = new Set(attackTypes);
  if (attackTypeSet.has(ProtectionAttackType.Honeypot)) {
    return AttackType.Honeypot;
  } else if (attackTypeSet.has(ProtectionAttackType.Impersonator)) {
    return AttackType.Impersonator;
  } else if (attackTypeSet.has(ProtectionAttackType.AirdropPattern)) {
    return AttackType.Airdrop;
  } else if (attackTypeSet.has(ProtectionAttackType.HighFees)) {
    return AttackType.HighFees;
  } else {
    return AttackType.Other;
  }
}
export function getCurrencySafetyInfo(safetyLevel, protectionInfo) {
  return {
    tokenList: getTokenListFromSafetyLevel(safetyLevel),
    attackType: getHighestPriorityAttackType(protectionInfo?.attackTypes),
    protectionResult: protectionInfo?.result ?? ProtectionResult.Unknown,
    blockaidFees: protectionInfo?.blockaidFees ? {
      buyFeePercent: protectionInfo.blockaidFees.buy ? protectionInfo.blockaidFees.buy * 100 : void 0,
      sellFeePercent: protectionInfo.blockaidFees.sell ? protectionInfo.blockaidFees.sell * 100 : void 0
    } : void 0
  };
}
export function gqlTokenToCurrencyInfo(token) {
  const { name, chain, address, decimals, symbol, project, feeData, protectionInfo } = token;
  const chainId = fromGraphQLChain(chain);
  const currency = buildCurrency({
    chainId,
    address,
    decimals,
    symbol,
    name,
    buyFeeBps: feeData?.buyFeeBps,
    sellFeeBps: feeData?.sellFeeBps
  });
  if (!currency) {
    return null;
  }
  return buildCurrencyInfo({
    currency,
    currencyId: currencyId(currency),
    logoUrl: project?.logoUrl,
    safetyInfo: getCurrencySafetyInfo(project?.safetyLevel, protectionInfo),
    // defaulting to not spam. currently this flow triggers when a user is searching
    // for a token, in which case the user probably doesn't expect the token to be spam
    isSpam: project?.isSpam ?? false
  });
}
export function usePersistedError(loading, error) {
  const persistedErrorRef = useRef();
  if (error || !loading) {
    persistedErrorRef.current = error;
  }
  return persistedErrorRef.current;
}
export function sortByName(unsortedBalances) {
  if (!unsortedBalances) {
    return [];
  }
  return unsortedBalances.sort((a, b) => {
    if (!a.currencyInfo.currency.name) {
      return 1;
    }
    if (!b.currencyInfo.currency.name) {
      return -1;
    }
    return a.currencyInfo.currency.name?.localeCompare(b.currencyInfo.currency.name);
  });
}
